<script lang="ts" setup>
import { Ref, ref, toRefs, onMounted, onUnmounted, watch } from 'vue';
import Column from 'primevue/column';
import DataTable, { DataTableFilterMeta } from 'primevue/datatable';
import { FilterMatchMode } from 'primevue/api';
import Dialog from 'primevue/dialog';
import { EMIT } from '@/enum';

interface AddItemsInterface {
  name: string;
  value: number;
}

interface AddPersonPropsInterface {
  items?: AddItemsInterface[] | [];
  title: string;
  className?: string;
  listHeaderText?: string;
  buttonText?: string;
  searchable?: boolean;
  searchProperty?: string;
  loading?: boolean;
  placeholder?: string;
  personType?: string;
}

const props = withDefaults(defineProps<AddPersonPropsInterface>(), {
  listHeaderText: 'Name',
  personType: 'people',
  items: undefined,
  buttonText: undefined,
  searchProperty: undefined,
  placeholder: 'enter search term',
  className: undefined,
  loading: false,
});
const emit = defineEmits<{
  (e: EMIT.CANCEL): void;
  (e: EMIT.LOAD_MORE_ITEMS): void;
  (e: EMIT.ADD_ITEMS, items: AddItemsInterface[]): void;
}>();

const { items, personType, placeholder, listHeaderText, loading } = toRefs(props);
const selectedItems = ref([]);
const scrollComponent = ref();
const modalVisible = ref(true);

onMounted(() => {
  setTimeout(() => {
    let content = document.getElementsByClassName('p-dialog-content');
    if (content.length) scrollComponent.value = content[0];
  }, 100);
});

onUnmounted(() => {
  stopScrollListener();
});

const stopScrollListener = () => {
  if (scrollComponent.value) scrollComponent.value.removeEventListener('scroll', handleScroll);
};

const startScrollListener = () => {
  if (scrollComponent.value) scrollComponent.value.addEventListener('scroll', handleScroll);
};

const handleScroll = () => {
  const element = scrollComponent.value;
  if (element.scrollTop >= element.scrollHeight - element.offsetHeight - 20) {
    //stop listening so it doesnt keep hitting the DB
    stopScrollListener();
    emit(EMIT.LOAD_MORE_ITEMS);
  }
};

/**
 * when the items list increases then start listening again for scroll
 */
watch(items, startScrollListener);

const addItems = (): void => {
  emit(EMIT.ADD_ITEMS, selectedItems.value);
  selectedItems.value = [];
  modalVisible.value = false;
};

const cancel = (): void => {
  emit(EMIT.CANCEL);
  selectedItems.value = [];
  modalVisible.value = false;
};

const onSearch = () => (selectedItems.value = []);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filter: Ref<any> = ref({
  global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  name: { value: '', matchMode: FilterMatchMode.STARTS_WITH },
} as DataTableFilterMeta);
</script>

<template>
  <Dialog v-model:visible="modalVisible" :draggable="false" :modal="true" class="dialog__container" @hide="cancel">
    <template #header>
      <div class="dialog__header flex flex-column">
        <h2 class="mb-3" v-text="props.title" />
        <IconField v-if="props.searchable" icon-position="left">
          <InputIcon>
            <Icon name="search" class="text-lg" />
          </InputIcon>
          <InputText
            v-model="filter.global.value"
            :placeholder="placeholder"
            class="p-inputtext-sm"
            @input="onSearch"
          />
        </IconField>
      </div>
    </template>
    <Loader v-if="loading" class="flex justify-content-center align-items-center h-full loader" />
    <DataTable
      v-model:filters="filter"
      v-model:selection="selectedItems"
      :global-filter-fields="['name']"
      :scrollable="true"
      :value="items"
      :virtual-scroller-options="{ itemSize: 52 }"
      data-key="value"
      responsive-layout="scroll"
      scroll-height="500px"
      table-style="min-width: 30rem"
    >
      <template v-if="items?.length">
        <Column body-style="max-width: 3rem" header-style="max-width: 3rem" selection-mode="multiple" />
        <Column :header="listHeaderText" field="name" sortable />
      </template>
      <template #empty>{{ !loading ? `No ${personType} to be added` : `Loading ${personType} to be added` }}</template>
    </DataTable>
    <template #footer>
      <AppButton text="Cancel" type="secondary" @click="cancel" />
      <AppButton
        :disabled="!selectedItems.length || !items?.length"
        :text="buttonText"
        type="primary"
        @click="addItems"
      />
    </template>
  </Dialog>
</template>

<style lang="scss" scoped></style>
